// ============================================================================
// PigContext — Phase 2.0.3
// ============================================================================
// Thin Provider that owns all pig-scoped useState hooks from App(). Helpers
// (submit, archive, edit openers, etc.) and the auto-save effects still live
// in App.tsx and will move to a pig feature folder in Phase 2 Round 6.
//
// External constants that feed initial state (INITIAL_FARROWING,
// INITIAL_BREEDERS, the breedTlStart computation) live in the data and date
// modules. AppProviders supplies them as props so this file has no upstream
// dependency on application composition.
//
// State owned:
//   breedingCycles     : array of breeding cycles
//   farrowingRecs      : array of farrowing records
//   boarNames          : { boar1, boar2 }
//   breedTlStart       : ISO date — breeding timeline view start
//   feederGroups       : array — pig feeder group / batch records
//   breeders           : array — sow/boar registry
//   breedOptions       : string[] — breed dropdown options
//   originOptions      : string[] — origin dropdown options
//
//   Breeding form: showBreedForm, editBreedId, breedForm
//   Farrowing form: showFarrowForm, editFarrowId, farrowForm, farrowFilter
//   Feeder form:   showFeederForm, editFeederId, feederForm, originalFeederForm
//   Sows UI:       sowSearch, expandedSow
//   Breeder form:  showBreederForm, editBreederId, breederForm
//   Trip form:     activeTripBatchId, tripForm, editTripId
// ============================================================================
import {createContext, useContext, useState} from 'react';
import type {ReactNode} from 'react';

import {DEFAULT_BOAR_NAMES, DEFAULT_BREED_OPTIONS, DEFAULT_ORIGIN_OPTIONS} from '../lib/pig.js';

const EMPTY_BREED_FORM = {
  group: '1',
  boar1Tags: '',
  boar2Tags: '',
  exposureStart: '',
  notes: '',
};

const EMPTY_FARROW_FORM = {
  sow: '',
  group: '1',
  farrowingDate: '',
  exposureStart: '',
  exposureEnd: '',
  sire: '',
  motheringQuality: '',
  demeanor: '',
  totalBorn: 0,
  deaths: 0,
  location: '',
  wentWell: '',
  didntGoWell: '',
  defects: '',
};

const EMPTY_FEEDER_FORM = {
  batchName: '',
  cycleId: '',
  giltCount: 0,
  boarCount: 0,
  startDate: '',
  originalPigCount: 0,
  perLbFeedCost: 0,
  legacyFeedLbs: 0,
  notes: '',
  status: 'active',
};

const EMPTY_TRIP_FORM = {
  date: '',
  pigCount: 0,
  liveWeights: '',
  hangingWeight: 0,
  notes: '',
};

const EMPTY_BREEDER_FORM = {
  tag: '',
  sex: 'Sow',
  group: '1',
  status: 'Sow Group',
  breed: '',
  origin: '',
  birthDate: '',
  lastWeight: '',
  purchaseDate: '',
  purchaseAmount: '',
  // SowsView renders `value={breederForm.notes}` as a CONTROLLED input, so the
  // canonical seed has to carry the key. Both openers happen to supply notes
  // before the form is shown, which is why nothing is visibly broken today —
  // but any path reaching the form from this seed alone would mount an
  // uncontrolled input and flip it to controlled on the first keystroke. The
  // sibling seed above already includes notes; this one was simply missed.
  notes: '',
};

// DEFAULT_BOAR_NAMES USED TO BE DECLARED HERE. It is imported from
// src/lib/pig.ts since /pig/breeding localized: the boot's ppp-boars-v1
// hydration is conditional (`if (store['ppp-boars-v1'])`), so pig.breeding.core
// needs the SAME empty-farm fallback this provider seeds with, and a second
// copy in the data layer could answer differently for a farm with no row.

// Props are the first-run seed values described in the header. Two are opaque
// record arrays (`unknown[]`) because their shapes are Podio-era exports that
// nothing in TypeScript describes yet and this module never reads a field off
// them. `breedTlStartInit` is different: it is a lazy initializer this module
// hands straight to useState, and AppProviders builds it from `toISO(...)`,
// which the converted src/lib/dateUtils.ts already declares returns a string —
// so `breedTlStart` gets a real type without anything being invented.
type PigProviderSeed = {
  initialFarrowing: unknown[];
  initialBreeders: unknown[];
  breedTlStartInit: () => string;
};

// Value type INFERRED from the builder below, never hand-authored.
//
// Every form default is a real module-scope literal, so the six form states,
// the farrowing filter, the boar names and both option lists arrive fully typed
// for free — that is the payoff for inferring rather than declaring. Only the
// record arrays and the "which row is open" ids stay `unknown`: those are DB /
// app_store rows and their ids, written by App.jsx and read by the pig views,
// never inspected here. `JSON.parse` on a localStorage blob likewise yields
// nothing this module knows, so the hydrated arrays keep the same element type
// as their empty fallbacks.
function usePigValue({initialFarrowing, initialBreeders, breedTlStartInit}: PigProviderSeed) {
  const [breedingCycles, setBreedingCycles] = useState<unknown[]>(() => {
    try {
      const r = localStorage.getItem('ppp-breeding-v1');
      return r ? JSON.parse(r) : [];
    } catch (e) {
      return [];
    }
  });
  const [farrowingRecs, setFarrowingRecs] = useState<unknown[]>(() => {
    try {
      const r = localStorage.getItem('ppp-farrowing-v1');
      return r ? JSON.parse(r) : initialFarrowing;
    } catch (e) {
      return initialFarrowing;
    }
  });
  const [boarNames, setBoarNames] = useState(() => {
    try {
      const r = localStorage.getItem('ppp-boars-v1');
      return r ? JSON.parse(r) : DEFAULT_BOAR_NAMES;
    } catch (e) {
      return DEFAULT_BOAR_NAMES;
    }
  });
  const [breedTlStart, setBreedTlStart] = useState(breedTlStartInit);

  // Breeding form
  const [showBreedForm, setShowBreedForm] = useState(false);
  const [editBreedId, setEditBreedId] = useState<unknown>(null);
  const [breedForm, setBreedForm] = useState(EMPTY_BREED_FORM);

  // Farrowing form
  const [showFarrowForm, setShowFarrowForm] = useState(false);
  const [editFarrowId, setEditFarrowId] = useState<unknown>(null);
  const [farrowForm, setFarrowForm] = useState(EMPTY_FARROW_FORM);
  const [farrowFilter, setFarrowFilter] = useState({group: 'all', sow: ''});

  // Feeder groups + form
  const [feederGroups, setFeederGroups] = useState<unknown[]>(() => {
    try {
      const r = localStorage.getItem('ppp-feeders-v1');
      return r ? JSON.parse(r) : [];
    } catch (e) {
      return [];
    }
  });
  // Pig batch and record pages now derive readiness from their route resource.
  // An empty feederGroups array is valid for a genuinely empty farm, so this
  // shared context intentionally publishes data only—not a global readiness
  // gate that could confuse an empty result with an unresolved request.
  const [showFeederForm, setShowFeederForm] = useState(false);
  const [editFeederId, setEditFeederId] = useState<unknown>(null);
  const [feederForm, setFeederForm] = useState(EMPTY_FEEDER_FORM);
  const [originalFeederForm, setOriginalFeederForm] = useState<unknown>(null);

  // Trip form
  const [activeTripBatchId, setActiveTripBatchId] = useState<unknown>(null);
  const [tripForm, setTripForm] = useState(EMPTY_TRIP_FORM);
  const [editTripId, setEditTripId] = useState<unknown>(null);

  // Sows UI
  const [sowSearch, setSowSearch] = useState('');
  const [expandedSow, setExpandedSow] = useState<unknown>(null);
  const [breeders, setBreeders] = useState(initialBreeders);
  // The two seeds are imported for the same reason DEFAULT_BOAR_NAMES is: App's
  // boot hydrates both keys conditionally, so `pig.sows.core` needs the SAME
  // empty-farm lists and a second copy in the data layer could answer
  // differently. Spread into a mutable array because the setters replace the
  // whole list and the exported constants are frozen.
  const [breedOptions, setBreedOptions] = useState<string[]>(() => [...DEFAULT_BREED_OPTIONS]);
  const [originOptions, setOriginOptions] = useState<string[]>(() => [...DEFAULT_ORIGIN_OPTIONS]);
  const [showBreederForm, setShowBreederForm] = useState(false);
  const [editBreederId, setEditBreederId] = useState<unknown>(null);
  const [breederForm, setBreederForm] = useState(EMPTY_BREEDER_FORM);

  return {
    breedingCycles,
    setBreedingCycles,
    farrowingRecs,
    setFarrowingRecs,
    boarNames,
    setBoarNames,
    breedTlStart,
    setBreedTlStart,
    showBreedForm,
    setShowBreedForm,
    editBreedId,
    setEditBreedId,
    breedForm,
    setBreedForm,
    showFarrowForm,
    setShowFarrowForm,
    editFarrowId,
    setEditFarrowId,
    farrowForm,
    setFarrowForm,
    farrowFilter,
    setFarrowFilter,
    feederGroups,
    setFeederGroups,
    showFeederForm,
    setShowFeederForm,
    editFeederId,
    setEditFeederId,
    feederForm,
    setFeederForm,
    originalFeederForm,
    setOriginalFeederForm,
    activeTripBatchId,
    setActiveTripBatchId,
    tripForm,
    setTripForm,
    editTripId,
    setEditTripId,
    sowSearch,
    setSowSearch,
    expandedSow,
    setExpandedSow,
    breeders,
    setBreeders,
    breedOptions,
    setBreedOptions,
    originOptions,
    setOriginOptions,
    showBreederForm,
    setShowBreederForm,
    editBreederId,
    setEditBreederId,
    breederForm,
    setBreederForm,
  };
}

type PigContextValue = ReturnType<typeof usePigValue>;

const PigContext = createContext<PigContextValue | null>(null);

export function PigProvider({children, ...seed}: PigProviderSeed & {children?: ReactNode}) {
  const value = usePigValue(seed);
  return <PigContext.Provider value={value}>{children}</PigContext.Provider>;
}

// Asserts the provider, and that assertion is what gives every consumer a
// non-null type. Not a new failure mode: the hook used to return `null` outside
// its provider and every consumer destructures the result immediately, so the
// app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function usePig(): PigContextValue {
  const value = useContext(PigContext);
  if (!value) throw new Error('usePig must be used within PigProvider');
  return value;
}
